import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { sportTypeIds } from '../../../../../utils/eventConstants';
import breadCrumbData from '../../../../SeoLocationLandingPage/breadCrumb.json';

const SeoURL = ({ event }) => {
    const baseUrls = {
        tennis: {
            eventsURL: '/tennis-tournaments',
            leaguesURL: '/tennis-leagues',
            rankings: {
                boys: '/boys-tennis-rankings',
                girls: '/girls-tennis-rankings',
                mens: '/mens-tennis-rankings',
                womens: '/womens-tennis-rankings',
            },
        },
        pickleball: {
            eventsURL: '/pickleball-tournaments',
            leaguesURL: '/pickleball-leagues',
        },
    };

    const location = event?.eventLocations?.[0] || {};
    const isTennisEvent = event?.sportTypeId === sportTypeIds.TENNIS;
    const isEventInUnitedStates = (location.countryName === 'United States' || location.countryCode3 === 'USA' || location.countryIoc === 'USA' || location.countryCode2 === 'US');

    const locationHelper = () => {
        let countryURL = '';
        let stateURL = '';
        let cityURL = '';
        let formattedCountryName = '';
        let formattedStateName = '';
        let formattedCityName = '';

        if (location) {
            const { countryName, stateName, cityName } = location;
            const formatString = (str) => str.toLowerCase().replace(/\s+/g, '-');

            if (countryName) {
                formattedCountryName = formatString(countryName);
                if (breadCrumbData[formattedCountryName]) {
                    countryURL = `/${formattedCountryName}`;
                }
            }

            if (countryURL && stateName) {
                formattedStateName = formatString(stateName);
                if (breadCrumbData[formattedCountryName][formattedStateName]) {
                    stateURL = `${countryURL}/${formattedStateName}`;
                }
            }

            if (stateURL && cityName) {
                formattedCityName = formatString(cityName);
                if (breadCrumbData[formattedCountryName][formattedStateName][formattedCityName]) {
                    cityURL = `${stateURL}/${formattedCityName}`;
                }
            }
        }

        return {
            countryURL,
            stateURL,
            cityURL,
        };
    };

    const locationHelperData = locationHelper();

    const generateURLs = (baseURLs, locationURL, locationName, sportName, isTennis) => {
        const urls = [
            {
                url: locationURL,
                title: `${locationName} ${sportName} Homepage`,
            },
            {
                url: locationURL + baseURLs.leaguesURL,
                title: `${locationName} ${sportName} Leagues`,
            },
            {
                url: locationURL + baseURLs.eventsURL,
                title: `${locationName} ${sportName} Tournaments`,
            },
        ];

        if (isTennis) {
            urls.push(
                {
                    url: locationURL + baseURLs.rankings.boys,
                    title: `${locationName} Boys' ${sportName} Rankings`,
                },
                {
                    url: locationURL + baseURLs.rankings.girls,
                    title: `${locationName} Girls' ${sportName} Rankings`,
                },
                {
                    url: locationURL + baseURLs.rankings.mens,
                    title: `${locationName} Men's ${sportName} Rankings`,
                },
                {
                    url: locationURL + baseURLs.rankings.womens,
                    title: `${locationName} Women's ${sportName} Rankings`,
                },
            );
        }

        return urls;
    };

    const sportName = isTennisEvent ? 'Tennis' : 'Pickleball';
    const sportBaseURLs = isTennisEvent ? baseUrls.tennis : baseUrls.pickleball;
    const countryURLs = locationHelperData.countryURL ? generateURLs(sportBaseURLs, locationHelperData.countryURL, location.countryName, sportName, isTennisEvent) : [];
    const stateURLs = locationHelperData.stateURL ? generateURLs(sportBaseURLs, locationHelperData.stateURL, location.stateName, sportName, isTennisEvent) : [];
    const cityURLs = locationHelperData.cityURL ? generateURLs(sportBaseURLs, locationHelperData.cityURL, location.cityName, sportName, isTennisEvent) : [];

    const urls = isEventInUnitedStates ? [...stateURLs, ...cityURLs] : countryURLs;

    if (urls.length === 0 || !location.countryName) {
        return null;
    }

    return (
        <div className="utr-card">
            <div className="mt14 p8">
                <ul>
                    {isEventInUnitedStates && location.cityName && cityURLs.length > 0 && (
                        <div className="p8">
                            <div className="fs-18 text-bold">{location.cityName}</div>
                            {cityURLs.map(urlObj => (
                                <li className="mt-2" key={urlObj.title}>
                                    <Link to={urlObj.url}>{urlObj.title}</Link>
                                </li>
                            ))}
                        </div>
                    )}

                    {isEventInUnitedStates && location.stateName && stateURLs.length > 0 && (
                        <div className="p8">
                            <div className="fs-18 text-bold">{location.stateName}</div>
                            {stateURLs.map(urlObj => (
                                <li className="mt-2" key={urlObj.title}>
                                    <Link to={urlObj.url}>{urlObj.title}</Link>
                                </li>
                            ))}
                        </div>
                    )}

                    {!isEventInUnitedStates && countryURLs.length > 0 && (
                        <div className="p8">
                            <div className="fs-18 text-bold">{location.countryName}</div>
                            {countryURLs.map(urlObj => (
                                <li className="mt-2" key={urlObj.title}>
                                    <Link to={urlObj.url}>{urlObj.title}</Link>
                                </li>
                            ))}
                        </div>
                    )}
                </ul>
            </div>
        </div>
    );
};

SeoURL.propTypes = {
    event: PropTypes.shape({
        sportTypeId: PropTypes.number.isRequired,
        eventLocations: PropTypes.arrayOf(PropTypes.shape({
            countryName: PropTypes.string.isRequired,
            stateName: PropTypes.string,
            cityName: PropTypes.string,
            countryCode2: PropTypes.string,
            countryCode3: PropTypes.string,
            countryIoc: PropTypes.string,
        })).isRequired,
    }).isRequired,
};

export default SeoURL;
